import React, { useEffect, useRef, useState } from 'react';
import { Send, FileText, Image, Paperclip, MessageCircle, MoreVertical, FileType, X, ChevronLeft, AlertTriangle } from 'lucide-react';
import uploadFileToS3 from '../../utils/uploadFileToS3';
import { fetchApprovedTemplates, canSendRegularMessage } from '../../utils/whatsappHelper';
import { useAuth } from '../../contexts/AuthContext';

// Utility functions
const getInitials = (name) => {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getAvatarColor = (id) => {
  const colors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-green-500',
    'bg-indigo-500',
    'bg-red-500',
  ];
  return colors[id % colors.length];
};

const formatTime = (date) => {
  if (!date) return '';
  // Convert to Date object if it's a string
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

// Message templates
const MESSAGE_TEMPLATES = [
  '👍 Sounds good!',
  '✅ Got it, thanks!',
  '⏰ Let me check and get back to you',
  '📞 Let\'s hop on a call',
  '💯 Perfect!',
];

// ============================================================================
// MESSAGE BUBBLE SUB-COMPONENT
// ============================================================================
const MessageBubble = ({ message }) => {
  const isUser = !message.incoming; // outgoing messages are from user

  const renderMessageContent = () => {
    switch (message.type) {
      case 'text':
      case null:
      case undefined:
        return <p className="text-sm leading-relaxed">{message.text}</p>;
      case 'image':
        return (
          <img
            src={message.text}
            alt="shared"
            className="max-w-xs rounded-lg"
          />
        );
      case 'file':
        return (
          <div className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <FileText size={20} className="text-blue-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{message.fileName || message.text}</p>
              {message.fileSize && <p className="text-xs text-gray-500">{message.fileSize}</p>}
            </div>
            <a 
              href={message.text} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 text-xs underline"
            >
              Download
            </a>
          </div>
        );
      case 'video':
        return (
          <div className="relative max-w-xs bg-black rounded-lg overflow-hidden">
            <div className="aspect-video flex items-center justify-center bg-gray-800">
              <p className="text-white text-center">📹 Video</p>
            </div>
          </div>
        );
      case 'audio':
        return (
          <div className="flex items-center gap-3 p-3">
            <button className="flex-shrink-0 bg-gray-200 dark:bg-gray-700 rounded-full p-2 hover:bg-gray-300">
              ▶️
            </button>
            <div className="flex-1">
              <div className="w-32 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
              <p className="text-xs text-gray-500 mt-1">0:45</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-white">
          {getInitials(message.name || 'Contact')}
        </div>
      )}
      <div className={`max-w-xs ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div
          className={`rounded-2xl px-4 py-2 ${
            isUser
              ? 'bg-blue-500 text-white rounded-br-none'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-none'
          }`}
        >
          {renderMessageContent()}
        </div>
        <span className="text-xs text-gray-500 mt-1 px-2">
          {formatTime(message.createdDate || message.timestamp)}
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// MESSAGE INPUT SUB-COMPONENT
// ============================================================================
const MessageInput = ({ onSendMessage, contactId, lastIncomingMessageDate }) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  // Check if user can send regular messages (24-hour window)
  const canSendMessage = canSendRegularMessage(lastIncomingMessageDate);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage({ 
        type: 'text', 
        text: message,
        channel: 'Whatsapp'
      });
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTemplateClick = (template) => {
    setMessage(template);
    setShowTemplates(false);
  };

  // Load WhatsApp templates
  const loadWhatsAppTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const templates = await fetchApprovedTemplates(user.tenantId, user._id);
      setWhatsappTemplates(templates);
      setShowTemplateModal(true);
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('No se pudieron cargar las plantillas de WhatsApp');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    
    // Initialize variables
    const initialVars = {};
    template.components?.forEach((component) => {
      if (component.type === 'BODY' && component.text) {
        const matches = component.text.match(/\{\{\d+\}\}/g);
        if (matches) {
          initialVars.body = new Array(matches.length).fill('');
        }
      }
      if (component.type === 'HEADER' && component.format === 'TEXT' && component.text) {
        const matches = component.text.match(/\{\{\d+\}\}/g);
        if (matches) {
          initialVars.header = new Array(matches.length).fill('');
        }
      }
    });
    
    setTemplateVariables(initialVars);
  };

  const handleSendTemplate = () => {
    if (!selectedTemplate) return;

    // Build template message text for display
    let templateText = selectedTemplate.name;
    
    // TODO: Send actual WhatsApp template via API
    // For now, just send as text message
    onSendMessage({
      type: 'text',
      text: `📋 Template: ${templateText}`,
      channel: 'Whatsapp'
    });

    // Reset and close
    setSelectedTemplate(null);
    setTemplateVariables({});
    setShowTemplateModal(false);
  };


  const handleFileUpload = async (file, type) => {
    if (!file) return;

    try {
      setIsUploading(true);
      
      // Upload file to S3
      const fileUrl = await uploadFileToS3({
        file,
        module: 'messages',
        recordId: contactId,
        tenantId: 1, // TODO: Get from auth context
        tipo: 'message'
      });

      // Send message with S3 URL matching backend schema
      const messageData = {
        type: type,
        text: fileUrl,
        channel: 'Whatsapp',
        fileName: file.name,
        fileSize: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
      };

      onSendMessage(messageData);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageClick = () => {
    imageInputRef.current?.click();
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'image');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'file');
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-3">
      {/* 24-hour Window Warning */}
      {!canSendMessage && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                Ventana de mensajería de 24 horas expirada
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                No puedes enviar mensajes regulares porque han pasado más de 24 horas desde el último mensaje del contacto. 
                Usa las <button onClick={() => setShowTemplates(true)} className="underline font-medium">Plantillas WhatsApp</button> para reactivar la conversación.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Templates */}
      {showTemplates && (
        <div className="flex flex-wrap gap-2">
          {MESSAGE_TEMPLATES.map((template, idx) => (
            <button
              key={idx}
              onClick={() => handleTemplateClick(template)}
              className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-sm rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {template}
            </button>
          ))}
          {/* WhatsApp Templates Button */}
          <button
            onClick={loadWhatsAppTemplates}
            disabled={loadingTemplates}
            className="px-3 py-1 bg-green-500 text-white text-sm rounded-full hover:bg-green-600 transition-colors flex items-center gap-1"
          >
            <FileType size={14} />
            {loadingTemplates ? 'Cargando...' : 'Plantillas WhatsApp'}
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="flex gap-2 items-end">
        {/* Hidden File Inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
        />
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Text Input */}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-24"
          rows="1"
        />

        {/* Action Buttons */}
        <div className="flex gap-2">
          {/* Image Button */}
          <button 
            onClick={handleImageClick}
            disabled={isUploading}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            title="Send image"
          >
            <Image size={20} className="text-gray-600 dark:text-gray-400" />
          </button>

          {/* File Button */}
          <button 
            onClick={handleFileClick}
            disabled={isUploading}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
            title="Send file"
          >
            <Paperclip size={20} className="text-gray-600 dark:text-gray-400" />
          </button>

          {/* Templates Button */}
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Quick templates"
          >
            <MessageCircle size={20} className="text-gray-600 dark:text-gray-400" />
          </button>

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={!message.trim() || isUploading || !canSendMessage}
            className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            title={!canSendMessage ? "24-hour window expired - use templates" : isUploading ? "Uploading..." : "Send message"}
          >
            {isUploading ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
      </div>

      {/* WhatsApp Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowTemplateModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Plantillas WhatsApp</h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X size={20} className="text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-140px)]">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : whatsappTemplates.length === 0 ? (
                <div className="text-center py-8">
                  <FileType size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 dark:text-gray-400">No hay plantillas aprobadas disponibles</p>
                </div>
              ) : !selectedTemplate ? (
                // Template List
                <div className="space-y-2">
                  {whatsappTemplates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">{template.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          template.category === 'MARKETING' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                          template.category === 'UTILITY' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {template.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {template.components?.find(c => c.type === 'BODY')?.text || 'Sin contenido'}
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                        <span>Idioma: {template.language}</span>
                        {template.components?.find(c => c.type === 'BODY')?.text?.includes('{{') && (
                          <span className="text-orange-500">• Requiere variables</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Template Variable Input
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setSelectedTemplate(null);
                      setTemplateVariables({});
                    }}
                    className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-600"
                  >
                    <ChevronLeft size={16} />
                    Volver a plantillas
                  </button>

                  {/* Template Preview */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">{selectedTemplate.name}</h4>
                    
                    {/* Header */}
                    {selectedTemplate.components?.find(c => c.type === 'HEADER') && (
                      <div className="mb-2">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {selectedTemplate.components.find(c => c.type === 'HEADER').text}
                        </p>
                      </div>
                    )}

                    {/* Body */}
                    {selectedTemplate.components?.find(c => c.type === 'BODY') && (
                      <div className="mb-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                          {selectedTemplate.components.find(c => c.type === 'BODY').text}
                        </p>
                      </div>
                    )}

                    {/* Footer */}
                    {selectedTemplate.components?.find(c => c.type === 'FOOTER') && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {selectedTemplate.components.find(c => c.type === 'FOOTER').text}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Variable Inputs */}
                  {Object.keys(templateVariables).length > 0 && (
                    <div className="space-y-3">
                      <h5 className="font-medium text-gray-900 dark:text-white">Variables de la plantilla</h5>
                      {Object.entries(templateVariables).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {key.replace('_', ' ')}
                          </label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => setTemplateVariables({ ...templateVariables, [key]: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder={`Ingrese ${key.replace('_', ' ').toLowerCase()}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShowTemplateModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSendTemplate}
                      disabled={Object.values(templateVariables).some(v => !v.trim())}
                      className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Enviar Plantilla
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CONVERSATION VIEW MAIN COMPONENT
// ============================================================================
const ConversationView = ({ contact, messages, onSendMessage }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get last incoming message date for 24-hour window check
  const lastIncomingMessage = messages
    .filter(msg => msg.sense === 'incoming' || msg.incoming)
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))[0];
  
  const lastIncomingMessageDate = lastIncomingMessage?.createdDate;

  if (!contact) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
        <div className="text-center">
          <MessageCircle size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400">Select a contact to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 h-full">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full ${getAvatarColor(contact.id)} flex items-center justify-center text-white font-semibold`}>
            {getInitials(contact.name)}
          </div>
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">{contact.name}</h2>
            <p className="text-xs text-gray-500 capitalize">{contact.status}</p>
          </div>
        </div>
        <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <MoreVertical size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <MessageInput 
        onSendMessage={onSendMessage} 
        contactId={contact.id} 
        lastIncomingMessageDate={lastIncomingMessageDate}
      />
    </div>
  );
};

export default ConversationView;